import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { CheckCircle2, XCircle, Loader2, Gift, Sparkles, ArrowLeft } from 'lucide-react';
import axios from 'axios';
// import { toast } from 'sonner'; // Temporarily disabled
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RegisterWithCode = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    code: ''
  });
  
  const [codeValidation, setCodeValidation] = useState(null);
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Validate code when user types it
  useEffect(() => {
    const validateCode = async () => {
      const code = formData.code.trim().toUpperCase();
      
      // Only validate if code has the right format
      if (code.length < 12 || !code.startsWith('TKALE-')) {
        setCodeValidation(null);
        return;
      }

      setIsValidatingCode(true);
      
      try {
        const response = await axios.post(`${API}/access-codes/validate`, {
          code: code
        });
        
        setCodeValidation(response.data);
      } catch (error) {
        setCodeValidation({
          valid: false,
          message: 'Erreur lors de la validation du code'
        });
      } finally {
        setIsValidatingCode(false);
      }
    };

    const debounce = setTimeout(() => {
      if (formData.code) {
        validateCode();
      }
    }, 500);

    return () => clearTimeout(debounce);
  }, [formData.code]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }

    if (!formData.username || formData.username.length < 2) {
      newErrors.username = 'Nom d\'utilisateur requis (min. 2 caractères)';
    }

    if (!formData.password || formData.password.length < 6) {
      newErrors.password = 'Mot de passe requis (min. 6 caractères)';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    if (!codeValidation?.valid) {
      newErrors.code = 'Code d\'accès valide requis';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      alert('Veuillez corriger les erreurs du formulaire');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/auth/register-with-code`, {
        email: formData.email,
        username: formData.username,
        password: formData.password,
        code: formData.code.toUpperCase()
      });

      const { access_token, user } = response.data;

      // Store token and user data
      localStorage.setItem('tkale_token', access_token);
      localStorage.setItem('tkale_user', JSON.stringify(user));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      alert(`Bienvenue ${user.username} ! Vous avez reçu ${user.credit_balance} crédits.`);
      
      // Redirect to dashboard immediately
      navigate('/dashboard');

    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'inscription';
      alert(message);
      
      if (message.includes('déjà enregistré')) {
        setErrors({ email: 'Cet email est déjà utilisé' });
      } else if (message.includes('Code')) {
        setErrors({ code: message });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-teal-900 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated background particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-cyan-400 rounded-full opacity-30"
            initial={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight
            }}
            animate={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
              opacity: [0.1, 0.5, 0.1]
            }}
            transition={{
              duration: 10 + Math.random() * 10,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        ))}
      </div>

      {/* Back button */}
      <Button
        variant="ghost"
        className="absolute top-4 left-4 text-cyan-300 hover:text-cyan-100 z-10"
        onClick={() => navigate('/')}
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        Retour
      </Button>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        <Card className="bg-white/10 backdrop-blur-md border-cyan-500/30 shadow-2xl">
          <CardHeader className="text-center">
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="text-6xl mb-4"
            >
              🐙
            </motion.div>
            <CardTitle className="text-3xl text-white mb-2">
              Inscription T-Kalé
            </CardTitle>
            <CardDescription className="text-cyan-200">
              Rejoignez l'écosystème T-Kalé avec votre code d'accès
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-cyan-100">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className={`bg-white/10 border-cyan-500/30 text-white placeholder:text-gray-400 ${
                    errors.email ? 'border-red-500' : ''
                  }`}
                  placeholder="votre@email.com"
                  disabled={isSubmitting}
                />
                {errors.email && (
                  <p className="text-red-400 text-sm">{errors.email}</p>
                )}
              </div>

              {/* Username */}
              <div className="space-y-2">
                <Label htmlFor="username" className="text-cyan-100">Nom d'utilisateur</Label>
                <Input
                  id="username"
                  type="text"
                  value={formData.username}
                  onChange={(e) => handleChange('username', e.target.value)}
                  className={`bg-white/10 border-cyan-500/30 text-white placeholder:text-gray-400 ${
                    errors.username ? 'border-red-500' : ''
                  }`}
                  placeholder="Votre nom"
                  disabled={isSubmitting}
                />
                {errors.username && (
                  <p className="text-red-400 text-sm">{errors.username}</p>
                )}
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-cyan-100">Mot de passe</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  className={`bg-white/10 border-cyan-500/30 text-white placeholder:text-gray-400 ${
                    errors.password ? 'border-red-500' : ''
                  }`}
                  placeholder="••••••••"
                  disabled={isSubmitting}
                />
                {errors.password && (
                  <p className="text-red-400 text-sm">{errors.password}</p>
                )}
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-cyan-100">
                  Confirmer le mot de passe
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleChange('confirmPassword', e.target.value)}
                  className={`bg-white/10 border-cyan-500/30 text-white placeholder:text-gray-400 ${
                    errors.confirmPassword ? 'border-red-500' : ''
                  }`}
                  placeholder="••••••••"
                  disabled={isSubmitting}
                />
                {errors.confirmPassword && (
                  <p className="text-red-400 text-sm">{errors.confirmPassword}</p>
                )}
              </div>

              {/* Access Code */}
              <div className="space-y-2">
                <Label htmlFor="code" className="text-cyan-100 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Code d'accès
                </Label>
                <div className="relative">
                  <Input
                    id="code"
                    type="text"
                    value={formData.code}
                    onChange={(e) => handleChange('code', e.target.value.toUpperCase())}
                    className={`bg-white/10 border-cyan-500/30 text-white placeholder:text-gray-400 font-mono pr-10 ${
                      errors.code ? 'border-red-500' : ''
                    } ${
                      codeValidation?.valid ? 'border-green-500' : ''
                    }`}
                    placeholder="TKALE-XXXXXX"
                    disabled={isSubmitting}
                    maxLength={12}
                  />
                  {/* Validation Icon */}
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {isValidatingCode && (
                      <Loader2 className="w-5 h-5 text-cyan-400 animate-spin" />
                    )}
                    {!isValidatingCode && codeValidation?.valid && (
                      <CheckCircle2 className="w-5 h-5 text-green-400" />
                    )}
                    {!isValidatingCode && codeValidation?.valid === false && (
                      <XCircle className="w-5 h-5 text-red-400" />
                    )}
                  </div>
                </div>
                {errors.code && (
                  <p className="text-red-400 text-sm">{errors.code}</p>
                )}
              </div>

              {/* Code Validation Feedback */}
              <AnimatePresence>
                {codeValidation && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <Alert
                      className={`${
                        codeValidation.valid
                          ? 'bg-green-500/20 border-green-500/50'
                          : 'bg-red-500/20 border-red-500/50'
                      }`}
                    >
                      <AlertDescription className="text-white">
                        <div>
                          {codeValidation.message}
                          {codeValidation.valid && codeValidation.benefits && (
                            <div className="mt-2 flex items-center gap-2">
                              <Gift className="w-4 h-4 text-yellow-400" />
                              <span className="font-semibold">
                                Vous recevrez {codeValidation.benefits.credits} crédits T-Kalé !
                              </span>
                            </div>
                          )}
                        </div>
                      </AlertDescription>
                    </Alert>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold py-6 text-lg"
                disabled={isSubmitting || !codeValidation?.valid}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Inscription en cours...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Créer mon compte
                  </>
                )}
              </Button>

              {/* Alternative */}
              <div className="text-center text-cyan-200 text-sm mt-4">
                <p>
                  Pas de code ?{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/register-free')}
                    className="text-cyan-300 hover:text-cyan-100 underline font-semibold"
                  >
                    Inscription gratuite
                  </button>
                </p>
                <p className="mt-2">
                  Déjà un compte ?{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="text-cyan-300 hover:text-cyan-100 underline font-semibold"
                  >
                    Se connecter
                  </button>
                </p>
              </div>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default RegisterWithCode;
